import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import EmptyState from '@app/src/components/EmptyState'
import Table from '@app/src/components/Table'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import useQueryFilters, { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import StandardQuestionHeader from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questions/StandardQuestionHeader'
import StandardQuestionRow from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questions/StandardQuestionRow'
import { QUESTIONNAIRE_FILTER_NAME } from '@app/src/pages/ResourceCollection/Filters/QuestionnaireFilter'
import StandardQuestionFilters from '@app/src/pages/ResourceCollection/Filters/StandardQuestionFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import ResourceCollection from '@app/src/pages/ResourceCollection/index'
import type { Question } from '@app/src/types/resourceExplorer'
import { FilterListOffOutlined } from '@mui/icons-material'
import { Box, Button } from '@mui/material'
import type { Dispatch, FC, SetStateAction } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'

const baseAllowedFilters: string[] = ['id', 'questionText', 'options.id']

export interface QuestionSelectionProps {
  selectedQuestionIds: Array<number>
  setSelectedQuestionIds: Dispatch<SetStateAction<number[]>>
  questionIdsAlreadyInQuestionnaire: Array<number>
}

interface StandardQuestionCollectionProps {
  selectionProps?: QuestionSelectionProps
  withLeftPadding?: boolean
}

const StandardQuestionCollection: FC<StandardQuestionCollectionProps> = ({ selectionProps, withLeftPadding }) => {
  const leftPadding = withLeftPadding ? 4 : 0
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  // The questionnaire filter is only offered while building a questionnaire (selection mode).
  const isSelection = Boolean(selectionProps)
  const allowedFilters = isSelection ? [...baseAllowedFilters, QUESTIONNAIRE_FILTER_NAME] : baseAllowedFilters
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [userFilters])
  const { clearQueryFilter } = useQueryFilters(allowedFilters)

  const implicitFilters = [{ name: 'isStandard', filters: [{ operator: Operators.EqualTo, value: true }] }]
  const { items, count, isLoading, isFetching, isError } = useFetchCollectionWithPost<Question>({
    key: FetchKey.Questions,
    endpoint: endpoints.questionCollection,
    payload: {
      sort: sorting,
      include: isSelection ? ['options.category', 'sections.questionnaireTemplate'] : ['options.category'],
      filter: [...implicitFilters, ...userFilters],
      pagination: {
        pageNumber: page,
        itemsPerPage: pageSize,
      },
    },
  })

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, question: Question) => {
    if (e.target.checked) {
      selectionProps?.setSelectedQuestionIds(prev => [...prev, question.id])
    } else {
      selectionProps?.setSelectedQuestionIds(prev => prev.filter(questionId => questionId !== question.id))
    }
  }

  return (
    <ResourceCollection
      title=""
      embedded
      filter={
        <Box pl={leftPadding}>
          <StandardQuestionFilters
            allowedFilters={allowedFilters}
            resetPage={resetPage}
            implicitFilters={implicitFilters}
            showQuestionnaireFilter={isSelection}
          />
        </Box>
      }
    >
      <Box display="flex" flexDirection="column" flexGrow={1} minHeight={0} pl={leftPadding}>
        <Table<Question>
          RowComponent={({ row }) => (
            <StandardQuestionRow
              question={row}
              selectionProps={
                selectionProps && {
                  selectedQuestionIds: selectionProps.selectedQuestionIds,
                  onCheckboxChange: onCheckboxChange,
                  questionIdsAlreadyInQuestionnaire: selectionProps.questionIdsAlreadyInQuestionnaire,
                }
              }
            />
          )}
          HeaderComponent={() => (
            <StandardQuestionHeader
              toggleSorting={toggleSorting}
              activeSorting={sorting}
              includeCheckboxHeader={Boolean(selectionProps)}
              selectionProps={
                selectionProps && {
                  selectedQuestionIds: selectionProps.selectedQuestionIds,
                  setSelectedQuestionIds: selectionProps.setSelectedQuestionIds,
                  onCheckboxChange: onCheckboxChange,
                  questionIdsAlreadyInQuestionnaire: selectionProps.questionIdsAlreadyInQuestionnaire,
                }
              }
              questionsInPage={items}
            />
          )}
          emptyState={
            <EmptyState
              iconComponent={FilterListOffOutlined}
              title={formatMessage({ id: 'general.noResults' })}
              description={formatMessage({ id: 'general.noResultSubtitle' })}
            >
              <Button variant="outlined" onClick={clearQueryFilter}>
                {formatMessage({ id: 'general.clearFilters' })}
              </Button>
            </EmptyState>
          }
          data={items}
          isLoading={isLoading || isFetching}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </Box>
    </ResourceCollection>
  )
}

export default StandardQuestionCollection
